import React, { useState, useEffect } from "react";
import config from "../../config";
import {
  TextField,
  Button,
  Typography,
  Box,
  Container,
  Paper,
  MenuItem,
  Grid,
  FormControlLabel,
  Switch,
  CircularProgress,
  IconButton,
} from "@mui/material";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast } from 'react-toastify';

const banquetStatusOptions = ["Available", "Booked", "Maintenance"];
const acTypeOptions = ["Split AC", "Central AC", "No AC"];
const cancellationPolicies = ["Flexible", "Moderate", "Strict", "Non-refundable"];

export default function AddBanquetHall() {
  const [form, setForm] = useState({
    price: "",
    tax: "",
    otherCharges: "",
    Totalroom: "", // backend name
    RoomSize: "",  // backend name
    acType: acTypeOptions[0],
    capacity: "",
    inclusions: "",
    description: "",
    status: "Available",
    refundable: true,
    cancellationPolicy: cancellationPolicies[0],
    hotelId: "",
  });
  const url = `${config.API_BASE_URL}${config.PRODUCT_LIST_API}`;
  const [hotels, setHotels] = useState([]);
  const [images, setImages] = useState([]);
  const [loadingHotels, setLoadingHotels] = useState(true);
  const [errors, setErrors] = useState({});

  // Fetch Hotels
  useEffect(() => {
    const fetchHotels = async () => {
      const token = sessionStorage.getItem("token");
      if (!token) return;
      try {
        const res = await fetch(
          `${config.api_base_url}/Home/GetHotelsdropdown`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error("Failed to fetch hotels");
        const data = await res.json();
        setHotels(data);
      } catch (err) {
        console.error(err);
      }
      setLoadingHotels(false);
    };
    fetchHotels();
  }, []);

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files).filter((f) => f instanceof File).map((file) => ({ file }));
    setImages((prev) => [...prev, ...files]);
  };

  const handleDeleteImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const validate = () => {
    const errs = {};
    if (!form.hotelId) errs.hotelId = "Hotel is required";
    if (!form.price || isNaN(form.price) || Number(form.price) <= 0) errs.price = "Valid Price is required";
    if (!form.tax || isNaN(form.tax)) errs.tax = "Valid Tax is required";
    if (!form.otherCharges || isNaN(form.otherCharges)) errs.otherCharges = "Valid Other Charges are required";
    if (!form.Totalroom || isNaN(form.Totalroom) || Number(form.Totalroom) <= 0) errs.Totalroom = "Valid Total Halls count is required";
    if (!form.acType) errs.acType = "AC Type is required";
    if (!form.capacity || isNaN(form.capacity) || Number(form.capacity) <= 0) errs.capacity = "Valid Capacity is required";
    if (!form.RoomSize || isNaN(form.RoomSize) || Number(form.RoomSize) <= 0) errs.RoomSize = "Valid Hall Size is required";
    if (!form.status) errs.status = "Status is required";
    if (!form.cancellationPolicy) errs.cancellationPolicy = "Cancellation Policy is required";

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      const token = sessionStorage.getItem("token");
      const formData = new FormData();

      formData.append("Price", form.price.toString());
      formData.append("Tax", form.tax.toString());
      formData.append("OtherCharges", form.otherCharges.toString());
      formData.append("Totalroom", form.Totalroom.toString()); // backend field
      formData.append("RoomSize", form.RoomSize.toString());   // backend field
      formData.append("AcType", form.acType);
      formData.append("Capacity", form.capacity.toString());
      formData.append("Inclusions", form.inclusions);
      formData.append("Description", form.description);
      formData.append("Status", form.status);
      formData.append("Refundable", form.refundable.toString());
      formData.append("CancellationPolicy", form.cancellationPolicy);
      formData.append("HotelId", form.hotelId.toString());

      images.forEach((imgObj) => formData.append("Images", imgObj.file));

      const response = await fetch(`${config.api_base_url}/Home/Addbanquethall`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        toast.error("Error: " + (errorData.message || response.statusText));
        return;
      }

      toast.success("Banquet Hall added successfully!");
      setForm({
        price: "",
        tax: "",
        otherCharges: "",
        Totalroom: "",
        RoomSize: "",
        acType: acTypeOptions[0],
        capacity: "",
        inclusions: "",
        description: "",
        status: "Available",
        refundable: true,
        cancellationPolicy: cancellationPolicies[0],
        hotelId: "",
      });
      setImages([]);
      setErrors({});
    } catch (err) {
      console.error(err);
      toast.error("Error saving banquet hall.");
    }
  };

  if (loadingHotels) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 6 }}>
      <Typography variant="h5" mb={3} fontWeight="bold" textAlign="center">
        Add New Banquet Hall
      </Typography>

      <Box component="form" noValidate onSubmit={handleSubmit} autoComplete="off">
        {/* Hotel Dropdown */}
        <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
          <TextField
            select
            label="Hotel"
            name="hotelId"
            value={form.hotelId}
            onChange={handleChange}
            error={!!errors.hotelId}
            helperText={errors.hotelId}
            fullWidth
            required
          >
            {hotels.map((h) => (
              <MenuItem key={h.id} value={h.id}>
                {h.HotelName || h.hotelName || "Unknown"}
              </MenuItem>
            ))}
          </TextField>
        </Paper>

        {/* AC Type */}
        <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
          <TextField
            select
            label="AC Type"
            name="acType"
            value={form.acType}
            onChange={handleChange}
            error={!!errors.acType}
            helperText={errors.acType}
            fullWidth
            required
          >
            {acTypeOptions.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </TextField>
        </Paper>

        {/* Status */}
        <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
          <TextField
            select
            label="Status"
            name="status"
            value={form.status}
            onChange={handleChange}
            error={!!errors.status}
            helperText={errors.status}
            fullWidth
            required
          >
            {banquetStatusOptions.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </TextField>
        </Paper>

        {/* Cancellation Policy */}
        <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
          <TextField
            select
            label="Cancellation Policy"
            name="cancellationPolicy"
            value={form.cancellationPolicy}
            onChange={handleChange}
            error={!!errors.cancellationPolicy}
            helperText={errors.cancellationPolicy}
            fullWidth
            required
          >
            {cancellationPolicies.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </TextField>
        </Paper>

        {/* Other Inputs */}
        <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField label="Price" name="price" type="number" value={form.price} onChange={handleChange} error={!!errors.price} helperText={errors.price} fullWidth required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Tax" name="tax" type="number" value={form.tax} onChange={handleChange} error={!!errors.tax} helperText={errors.tax} fullWidth required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Other Charges" name="otherCharges" type="number" value={form.otherCharges} onChange={handleChange} error={!!errors.otherCharges} helperText={errors.otherCharges} fullWidth required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Total Halls" name="Totalroom" type="number" value={form.Totalroom} onChange={handleChange} error={!!errors.Totalroom} helperText={errors.Totalroom} fullWidth required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Capacity" name="capacity" type="number" value={form.capacity} onChange={handleChange} error={!!errors.capacity} helperText={errors.capacity} fullWidth required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Hall Size (sq ft)" name="RoomSize" type="number" value={form.RoomSize} onChange={handleChange} error={!!errors.RoomSize} helperText={errors.RoomSize} fullWidth required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Inclusions" name="inclusions" value={form.inclusions} onChange={handleChange} multiline minRows={2} placeholder="Comma separated amenities" fullWidth />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Description" name="description" value={form.description} onChange={handleChange} multiline minRows={2} fullWidth />
            </Grid>
            <Grid item xs={12} sm={6} sx={{ display: "flex", alignItems: "center" }}>
              <FormControlLabel control={<Switch checked={form.refundable} onChange={handleChange} name="refundable" />} label="Refundable" />
            </Grid>
          </Grid>
        </Paper>

        {/* Image Upload */}
        <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
          <Typography variant="h6" mb={2} fontWeight="bold">Upload Images</Typography>
          <Button variant="contained" component="label" startIcon={<PhotoCamera />} fullWidth>
            Upload Images
            <input hidden type="file" accept="image/*" multiple onChange={handleImageChange} />
          </Button>
          {images.length > 0 && (
            <Box sx={{ display: "flex", overflowX: "auto", gap: 2, py: 2, mt: 2 }}>
              {images.map((imgObj, idx) => {
                if (!imgObj?.file) return null;
                const url = URL.createObjectURL(imgObj.file);
                return (
                  <Box key={idx} sx={{ position: "relative", minWidth: 100, width: 100, height: 100, border: "1px solid #ccc", borderRadius: 2, overflow: "hidden", flexShrink: 0 }}>
                    <img src={url} alt={`Banquet ${idx}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} onLoad={() => URL.revokeObjectURL(url)} />
                    <IconButton size="small" color="error" onClick={() => handleDeleteImage(idx)} sx={{ position: "absolute", top: 5, right: 5, background: "white" }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                );
              })}
            </Box>
          )}
        </Paper>

        {/* Submit */}
        <Button type="submit" variant="contained" fullWidth sx={{ mt: 2, py: 1.5 }}>
          Add Banquet Hall
        </Button>
      </Box>
    </Container>
  );
}
